import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import "@/App.css";
import { useAuthStore } from "@/lib/stores";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OTPLoginModal from "@/components/OTPLoginModal";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Orders from "@/pages/Orders";
import MyVouchers from "@/pages/MyVouchers";
import OccasionPage from "@/pages/OccasionPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { BlogList, BlogPost } from "@/pages/Blog";
import { About, HowItWorks, FAQs, Contact, Terms, Privacy, RefundPolicy } from "@/pages/StaticPages";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);
    return null;
}

function RouteWithChrome({ children, hideChrome = false }) {
    if (hideChrome) return children;
    return (
        <>
            <Header />
            <main className="min-h-[60vh]">{children}</main>
            <Footer />
            <OTPLoginModal />
            <CartDrawer />
        </>
    );
}

function App() {
    const { token, fetchMe, setHydrated } = useAuthStore();

    useEffect(() => {
        if (token) fetchMe();
        setHydrated();
        // inject Razorpay checkout script
        if (!document.getElementById("rzp-checkout")) {
            const s = document.createElement("script");
            s.id = "rzp-checkout";
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.async = true;
            document.body.appendChild(s);
        }
    }, [token, fetchMe, setHydrated]);

    return (
        <BrowserRouter>
            <Toaster position="top-center" toastOptions={{
                style: { background: "#0B1437", color: "#fff", borderRadius: 12, fontFamily: "Epilogue, sans-serif", fontSize: 14 },
                success: { iconTheme: { primary: "#E4C86E", secondary: "#0B1437" } },
            }} />
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<RouteWithChrome><Home /></RouteWithChrome>} />
                <Route path="/catalog" element={<RouteWithChrome><Catalog /></RouteWithChrome>} />
                <Route path="/brand/:slug" element={<RouteWithChrome><ProductDetail /></RouteWithChrome>} />
                <Route path="/category/:name" element={<RouteWithChrome><Catalog /></RouteWithChrome>} />
                <Route path="/gifts/:slug" element={<RouteWithChrome><OccasionPage /></RouteWithChrome>} />
                <Route path="/checkout" element={<RouteWithChrome><Checkout /></RouteWithChrome>} />
                <Route path="/orders" element={<RouteWithChrome><Orders /></RouteWithChrome>} />
                <Route path="/orders/:orderId/success" element={<RouteWithChrome><OrderSuccess /></RouteWithChrome>} />
                <Route path="/vouchers" element={<RouteWithChrome><MyVouchers /></RouteWithChrome>} />
                {/* SEO: Blog */}
                <Route path="/blog" element={<RouteWithChrome><BlogList /></RouteWithChrome>} />
                <Route path="/blog/:slug" element={<RouteWithChrome><BlogPost /></RouteWithChrome>} />
                {/* SEO: Static pages */}
                <Route path="/about" element={<RouteWithChrome><About /></RouteWithChrome>} />
                <Route path="/how-it-works" element={<RouteWithChrome><HowItWorks /></RouteWithChrome>} />
                <Route path="/faqs" element={<RouteWithChrome><FAQs /></RouteWithChrome>} />
                <Route path="/contact" element={<RouteWithChrome><Contact /></RouteWithChrome>} />
                <Route path="/terms" element={<RouteWithChrome><Terms /></RouteWithChrome>} />
                <Route path="/privacy" element={<RouteWithChrome><Privacy /></RouteWithChrome>} />
                <Route path="/refund-policy" element={<RouteWithChrome><RefundPolicy /></RouteWithChrome>} />
                <Route path="/admin/login" element={<RouteWithChrome hideChrome><AdminLogin /></RouteWithChrome>} />
                <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
